import { useMutation, useQuery, useQueryClient } from "react-query";
import MetricProcess from "../pages/takeoff/layouts/processes/models/metric-process.ts";
import { fetchItem, putItem } from "../queries/queries-helper";
import ENDPOINTS from "../services/endpoints.ts";
import DashboardProcessDto, { PROCESS_DTO_MAPPING } from "../models/interfaces/dtos/dashboard-process-dto.ts";
import FlowMode from "@/models/enums/flow-mode.ts";
import StatsProcessDto from "@/models/interfaces/dtos/stats-process-dto";

type StatsProcessUpdate = Partial<StatsProcessDto> | ((current: StatsProcessDto) => Partial<StatsProcessDto>);

const useDashboardProcess = (projectId: string) => {
    const queryClient = useQueryClient();
    const dashboardProcessStatusQuery = useQuery(
        ["dashboard-process-status", projectId],
        async () => await fetchItem<DashboardProcessDto>(ENDPOINTS.DASHBOARD_PROCESS_STATUS(projectId)) ?? undefined,
        {
            enabled: !!projectId,
            staleTime: 3 * 60 * 1000,
        }
    );

    const updateDashboardProcessStatus = useMutation({
        mutationFn: async (update: StatsProcessUpdate) => {
            // Le PUT remplace tout le statut : relire les révisions avant de les fusionner.
            const current = await fetchItem<DashboardProcessDto>(ENDPOINTS.DASHBOARD_PROCESS_STATUS(projectId))
                ?? await fetchItem<StatsProcessDto>(ENDPOINTS.DASHBOARD_INITIAL_PROCESS_STATUS(projectId));
            
            // if (!current) throw new Error("Impossible d'initialiser le statut du projet.");
            if (!current) return;

            const stats: StatsProcessDto = {
                revision: current.revision,
                is2D: current.is2D,
                is3D: current.is3D,
                revMetresDepuisPDF: current.revMetresDepuisPDF,
                revPreparationCoques: current.revPreparationCoques,
                revDestinationsCoques: current.revDestinationsCoques,
                revSurfaces: current.revSurfaces,
                revMetresCarbone: current.revMetresCarbone,
                revMetresQuantitatifGOE: current.revMetresQuantitatifGOE,
                revQuantitatifLotMex: current.revQuantitatifLotMex,
                revQuantitatifLotMenuiseries: current.revQuantitatifLotMenuiseries,
                revAmenagementsAppartements: current.revAmenagementsAppartements,
                revSurfacesPrecisesSOE: current.revSurfacesPrecisesSOE,
                revCalcul1: current.revCalcul1,
                revCalcul2: current.revCalcul2,
                revCalcul3: current.revCalcul3
            };

            await putItem(ENDPOINTS.DASHBOARD_UPDATE_PROCESS_STATUS(projectId), {
                ...stats,
                ...(typeof update === "function" ? update(stats) : update)
            });
        },
        onSuccess: async () => {
            // Les hasCalcul* sont calculés par l'API, pas déduits côté client.
            await queryClient.invalidateQueries(["dashboard-process-status", projectId], { exact: true });
        }
    });

    const applyDashboardProcessStatus = (
        metrics: MetricProcess[],
        projectFlowMode: FlowMode,
        dto?: DashboardProcessDto
    ): MetricProcess[] => {
        if (!dto) return metrics;

        return metrics.map(metric => {
            const mapping = PROCESS_DTO_MAPPING[metric.keyName];

            if (!mapping) return metric;

            return new MetricProcess(metric.keyName, projectFlowMode, {
                ...metric,
                
                is2DDone: metric.is2D && (mapping.is2D ? Boolean(dto[mapping.is2D]) : metric.is2DDone),
                is3DDone: metric.is3D && (mapping.is3D ? Boolean(dto[mapping.is3D]) : metric.is3DDone)
            });
        });
    }

    return {
        dashboardProcessStatusQuery,
        updateDashboardProcessStatus,
        applyDashboardProcessStatus
    }
}

export default useDashboardProcess;
